import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AddLogRequest, AddLogResponse } from '@models/shared.models';
import { LoginService } from './login.service';
import { LoginType } from '@models/registration.models';

type UserAction = 'SKIP_SHARE' | 'CANCEL_SHARE' | 'DECLINE_SHARE' | 
    'CANCEL_ADD_METER' | 'SKIP_ADD_METER' | 'DECLINE_ADD_METER' | 'LOGOUT' | 
    'PLATFORM_SHARE' | 'EMAIL_SHARE' | 'LINK_SHARE' | 'ADD_METER_ADDRESS_SELECTED';
type LoginAction = 'MAGIC_LINK' | 'SEND_OTP' | 'VERIFY_OTP' | 'RESEND_EMAIL' | 'RESEND_OTP';
@Injectable({
  providedIn: 'root'
})
export class LogService {
  private baseUrlCore = `${environment.baseUrl}/core`;
  private loginService = inject(LoginService);
  private http = inject(HttpClient);

  private get loginUserId(): string  {
    return this.loginService.loginUser?.email ?? 'NOT_LOGGED_IN';
  }

  /**
   * Add log entry to the system
   * @param logData - Log data to be sent
   * @returns Observable<AddLogResponse>
   */
  addLog(logData: AddLogRequest): Observable<AddLogResponse> {
    const url = `${this.baseUrlCore}/v1/add-log`;

    return this.http.post<AddLogResponse>(url, logData).pipe(
      catchError(error => {
        console.error('Error adding log:', error);
        throw error;
      })
    );
  }

  /**
   * Add page view log
   * @param pageName - Name of the page being viewed
   * @param metadata - Additional metadata for the page view
   * @returns Observable<AddLogResponse>
   */
  addPageViewLog(pageName: string, metadata: any): Observable<AddLogResponse> {
    const logRequest: AddLogRequest = {
      apiName: 'page-view',
      source: 'ui',
      success: true,
      payload: {
        pageName: pageName,
        ...metadata
      },
      userId: this.loginUserId,
      tracker: environment.trackerId
    };

    return this.addLog(logRequest);
  }

  /**
   * Add page view log with automatic subscription handling
   * @param pageName - Name of the page being viewed
   * @param metadata - Additional metadata for the page view
   */
  logPageView(pageName: string, metadata: any): void {
    this.addPageViewLog(pageName, metadata).subscribe();
  }

  addLoginLog(email: string, action: LoginAction): void {
    const logRequest: AddLogRequest = {
      apiName: 'user-login',
      source: 'ui',
      success: true,
      payload: action.toString(),
      userId: email,
      tracker: environment.trackerId
    };

    this.addLog(logRequest).subscribe();
  }

  addUserActionLog(action: UserAction, metadata?: any): void {
    const logRequest: AddLogRequest = {
      apiName: 'user-action',
      source: 'ui',
      success: true,
      payload: {
        action,
        metadata
      },
      userId: this.loginUserId,
      tracker: environment.trackerId
    };

    this.addLog(logRequest).subscribe();
  }

  addConsentActionLog(consentType: string, customPayload?: Record<string, any>): void {
    const logRequest: AddLogRequest = {
      apiName: 'consent-action',
      source: 'ui',
      success: true,
      payload: {
        consentType,
        ...customPayload
      },
      userId: this.loginUserId,
      tracker: environment.trackerId
    };

    this.addLog(logRequest).subscribe();
  }

  addShareActionLog(shareType: string, sharePayload: any): void {
    const logRequest: AddLogRequest = {
      apiName: 'share-action',
      source: 'ui',
      success: true,
      payload: {
        shareType,
        sharePayload
      },
      userId: this.loginUserId,
      tracker: environment.trackerId
    };

    this.addLog(logRequest).subscribe();
  }

  addRegisterLog(registerPayload: any): void {
    const logRequest: AddLogRequest = {
      apiName: 'user-register',
      source: 'ui',
      success: true,
      payload: registerPayload,
      userId: this.loginUserId,
      tracker: environment.trackerId
    };

    this.addLog(logRequest).subscribe();
  }

  addErrorLog(errorType: string, errorPayload: any): void {
    const logRequest: AddLogRequest = {
      apiName: 'error-log',
      source: 'ui',
      success: false,
      payload: {
        errorType,
        errorPayload
      },
      userId: this.loginUserId,
      tracker: environment.trackerId
    };

    this.addLog(logRequest).subscribe();
  }
}

